import { useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useParams } from "react-router";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  delta,
  formatDeliveryDate,
  variancePercent,
} from "@common/estimateAccuracy";
import useGetProjectReview from "../api/useGetProjectReview";
import ServiceDeliveryTable from "./common/ServiceDeliveryTable";
import { useStyles } from "./styles";

const EMPTY_MESSAGE =
  "No PSA delivery data has been recorded for this project.";

// Deliberately not EMPTY_MESSAGE: "no delivery data" is a finding about the
// project, and saying it when the request simply failed reports an absence we
// have not established.
const ERROR_MESSAGE = "This project review could not be loaded.";

const ProjectReviewShow = () => {
  const styles = useStyles();
  const { id } = useParams();
  const { review, isLoading, error } = useGetProjectReview(id ?? "");
  const [errorDismissed, setErrorDismissed] = useState<boolean>(false);

  if (isLoading) return <ScopeStackSpinner />;

  // `review` stays null on a failed request while isLoading goes false, so
  // falling through to the spinner here would hang the page forever.
  if (error || !review) {
    return (
      <div className={styles.wrapper}>
        {error && !errorDismissed && (
          <ToastAlert
            variant="danger"
            text={ERROR_MESSAGE}
            onClose={() => setErrorDismissed(true)}
          />
        )}
        {/* The toast already says this while it's showing; a second copy
            underneath would read as two errors instead of one -- and the card
            that carries it would sit there as an empty white box. */}
        {(!error || errorDismissed) && (
          <Row>
            <Col>
              <Card className={styles.card}>
                <p className={styles.empty}>{ERROR_MESSAGE}</p>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    );
  }

  const services = review.services ?? [];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <div className={styles.reviewHeader}>
            <h1 className={styles.title}>{review.projectName}</h1>
            <div className={styles.subheading}>{review.clientName}</div>
            {review.lastCompletedOn && (
              <div className={styles.subheading}>
                {`Last completed ${formatDeliveryDate(review.lastCompletedOn)}`}
              </div>
            )}
          </div>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            {/* Absence is a property of the project (nothing has ever been
                tracked), not of this render's services array: a settled
                project with services still summarizes from review's own
                totals even before the table's rows are considered. */}
            {review.servicesTracked === 0 ? (
              <p className={styles.empty}>{EMPTY_MESSAGE}</p>
            ) : (
              <>
                <div className={styles.summary}>
                  <div className={styles.figure}>
                    <span className={styles.figureLabel}>Sold Hrs</span>
                    <span className={styles.figureValue}>
                      {Math.round(review.estimatedHours)}
                    </span>
                  </div>
                  <div className={styles.figure}>
                    <span className={styles.figureLabel}>Actual Hrs</span>
                    <span className={styles.figureValue}>
                      {Math.round(review.actualHours)}
                    </span>
                  </div>
                  <div className={styles.figure}>
                    <span className={styles.figureLabel}>Delta</span>
                    <span className={styles.figureValue}>
                      {delta(review.deltaHours)}
                    </span>
                  </div>
                  <div className={styles.figure}>
                    <span className={styles.figureLabel}>Variance</span>
                    <span className={styles.figureValue}>
                      {variancePercent(review.variancePercentage)}
                    </span>
                  </div>
                </div>

                {/* The figures above cover settled services only, so the count
                    that says how much of the project they describe travels
                    with them. */}
                <div className={styles.coverage}>
                  <span>
                    {`${review.servicesSettled} of ${review.servicesTracked} services settled`}
                  </span>
                  {review.unsettledServices > 0 && (
                    <span className={styles.caveat}>
                      Hours cover settled services only
                    </span>
                  )}
                </div>

                <ServiceDeliveryTable services={services} />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectReviewShow;
